import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ApiService} from "@ctms/api.service";
import {Survey, WeeklySummary} from "@shared/api/models/query/patient-statistics.model";
import {toTrendDirection, TrendIconComponent} from '@shared/components/trend-icon/trend-icon.component';
import {TrendDirection} from '@shared/api/models/query/patient-daily-snapshot.model';

const NON_FLAGGED_SEVERITIES = ['minimal', 'normal', 'none'];

@Component({
    selector: 'cdss-patient-overview-v2-surveys',
    imports: [CommonModule, TrendIconComponent],
    providers: [ApiService],
    standalone: true,
    templateUrl: './patient-overview-v2-surveys.component.html',
    styleUrl: './patient-overview-v2-surveys.component.scss'
})
export class PatientOverviewV2SurveysComponent {

    @Input() type = 'patient';
    private _weeklySummary: WeeklySummary

    disabled: boolean = false
    isFlagged: boolean = false

    phq9: Survey | undefined
    ed15: Survey | undefined
    gad: Survey | undefined

    @Input()
    set weeklySummary(weeklySummary: WeeklySummary) {
        this._weeklySummary = weeklySummary
        if (!weeklySummary) return
        this.disabled = weeklySummary.surveys.length === 0

        if (this.type === 'patient') {
            // Dashboard patient card is ED-15 only — overall score + trend arrow vs
            // last week (UNIVAPLAT-3040, corrected post-OQ-FE3). No PHQ-9/GAD-7
            // lookup, no subscale breakdown, no Q11-Q15 counts on this card — that
            // detail lives on the weekly summary page (UNIVAPLAT-3035).
            //
            // Post-cutover patients answer the ED-15 instrument through the Unified
            // Weekly Check-in (UWC) instead of the legacy E15 check — the backend
            // already returns it under code 'UWC' (PatientStatisticsService::getSurveys),
            // so this lookup must match both.
            this.ed15 = this.getSurvey('E15', 'UWC')
        } else {
            this.phq9 = this.getSurvey('PHQ', 'PHQ-C')
            this.gad = this.getSurvey('GAD', 'GAD-C')
        }
        this.isFlagged = this.computeIsFlagged()
    }
    get weeklySummary(): WeeklySummary {
        return this._weeklySummary
    }

    getSurvey(code1: string, code2: string): Survey | undefined {
        return this.weeklySummary.surveys.find(survey => survey.code === code1 || survey.code === code2)
    }

    private computeIsFlagged(): boolean {
        const visibleSurveys = (this.type === 'patient' ? [this.ed15] : [this.phq9, this.gad])
            .filter((s): s is Survey => s !== undefined)
        return visibleSurveys.some(survey =>
            !NON_FLAGGED_SEVERITIES.includes((survey.severity ?? '').toLowerCase())
        )
    }

    /** Explicit API-to-icon mapping: no survey (or an unrecognised trend) is unknown (null), never stable. */
    trendOf(survey: Survey | undefined): TrendDirection | null {
        return (this.disabled || survey == undefined) ? null : toTrendDirection(survey.trend)
    }
}
